import { BaseSubscriptionServiceResponseJson } from './types/BaseSubscriptionServiceResponseJson'
import { CheckoutBillingAddress } from '../../types/CheckoutBillingAddress'
import { PlanCostSummary, PlanCostSummaryType } from '../../types/PlanCostSummary'
import { PlanExtraDetail, PlanExtraResponse } from '@dtx-company/true-common/src/types/Plan'
import { fetchSubscriptionServiceUrl } from './fetchSubscriptionServiceUrl'
import isNil from 'lodash/isNil'

export interface GetPlanCostSummaryOptions {
  token: string
  newPlanId?: string
  paymentMethodId?: string
  promoCode?: string
  address?: Partial<CheckoutBillingAddress>
  planExtraDetails?: PlanExtraDetail[]
  shouldActivate?: boolean
}

export interface GetPlanCostSummaryJSON extends BaseSubscriptionServiceResponseJson {
  additionalPlanExtras: PlanExtraResponse[] | null
  annualDiscount: number | null
  basePlanExtras: PlanExtraResponse[] | null
  basePrice: number
  couponDiscount: number | null
  creditApplied: number | null
  downgradePeriodEndDate: string | null
  grandTotal: number
  prorationApplied: number | null
  subtotal: number
  tax: number | null
  type: string
}

function toDate(value: string | null): Date | undefined {
  if (!value) return undefined
  const date = new Date(value)
  if (!date || isNaN(date.valueOf())) {
    throw new Error(`The /summary endpoint provided a date in an invalid format: ${value}`)
  }
  return date
}

function toNumber(value: number | null): number | undefined {
  if (isNil(value)) return undefined
  if (typeof value !== 'number') {
    throw new Error(`The /summary endpoint provided a value that's not a number: ${value}`)
  }
  return value
}

function toType(value: string | null): PlanCostSummaryType {
  if (!value) throw new Error(`The /summary endpoint did not provide a type`)
  const mapping: Record<string, PlanCostSummaryType | undefined> = {
    UPGRADE: PlanCostSummaryType.UPGRADE,
    DOWNGRADE: PlanCostSummaryType.DOWNGRADE,
    NEW: PlanCostSummaryType.NEW,
    SAME: PlanCostSummaryType.SAME,
    EXTRAS: PlanCostSummaryType.EXTRAS,
    ACTIVATE: PlanCostSummaryType.ACTIVATE
  }
  const type = mapping[value]
  if (!type) {
    throw new Error(`The /summary endpoint provided an unknown type: ${value}`)
  }
  return type
}

function buildPlanCostSummaryFromJSON(json: GetPlanCostSummaryJSON): PlanCostSummary {
  return {
    additionalPlanExtras: json.additionalPlanExtras || [],
    annualDiscountInCents: toNumber(json.annualDiscount),
    basePlanExtras: json.basePlanExtras || [],
    basePriceInCents: toNumber(json.basePrice) || 0,
    couponDiscountInCents: toNumber(json.couponDiscount),
    creditAppliedInCents: toNumber(json.creditApplied),
    downgradePeriodEndDate: toDate(json.downgradePeriodEndDate),
    grandTotalInCents: toNumber(json.grandTotal) || 0,
    prorationAppliedInCents: toNumber(json.prorationApplied),
    subtotalInCents: toNumber(json.subtotal) || 0,
    taxInCents: toNumber(json.tax),
    type: toType(json.type)
  }
}

/**
 * Calls the subscription service GET /summary endpoint.
 *
 * May be used as a fetcher for useSWR or by itself for SSR.
 *
 * @docs https://github.com/dtx-company/subscription-service#summary
 */
export async function getPlanCostSummary(
  options: GetPlanCostSummaryOptions
): Promise<PlanCostSummary> {
  // use paymentMethodId if given, else use address fields
  // api uses these to determine tax calculations so expects one or the other
  const summaryOptions = {
    planId: options.newPlanId,
    couponCode: options.promoCode,
    ...(options.planExtraDetails && { planExtras: options.planExtraDetails }),
    ...(options.shouldActivate && { shouldActivate: options.shouldActivate }),
    ...(options.paymentMethodId
      ? { paymentMethodId: options.paymentMethodId }
      : {
          country: options.address?.country,
          street1: options.address?.street1,
          street2: options.address?.street2,
          city: options.address?.city,
          region: options.address?.region,
          postalCode: options.address?.postalCode
        })
  }

  const response = await fetchSubscriptionServiceUrl<GetPlanCostSummaryJSON>({
    method: 'POST',
    token: options.token,
    url: '/v2/summary',
    body: summaryOptions
  })

  if (response.status !== 200) {
    // v2 has a json.message instead
    // TODO add a BaseSubscriptionServiceResponseJson2 to model v2 responses
    const error =
      response.json && 'message' in response.json
        ? response.json.message
        : response.json?.statusDescription
    if (error === 'downgrade-not-supported') {
      throw new Error('Downgrade not supported')
    } else {
      throw new Error(`Received http status ${response.status} from /summary endpoint`)
    }
  }

  if (!response.json) {
    throw new Error(`The /summary endpoint did not return a json payload`)
  }

  const planCostSummary = buildPlanCostSummaryFromJSON(response.json)
  return planCostSummary
}
